import type { CatalogResource } from '../types/resourceCatalog';
import { catalogInterestQuery } from './catalogTaxonomy';
import { isInterestTopic } from './placeInterests';

export const NEW_FOR_YOU_LIMIT = 4;
/** How many recent views feed interest-tag weights (catalog fetch). */
export const NEW_FOR_YOU_RECENT_SIGNAL = 40;
/**
 * How many recent views to load for unchanged-since-view exclusion.
 * Matches the server GET default/max for /api/me/recent-resources.
 */
export const NEW_FOR_YOU_VIEW_HISTORY_LIMIT = 500;
/** How many top-weighted topics to send to the catalog query. */
export const NEW_FOR_YOU_TOPIC_QUERY_LIMIT = 6;
export const NEW_FOR_YOU_CANDIDATE_PAGE = 40;

const PROFILE_TOPIC_WEIGHT = 1;
const RECENT_VIEW_BASE_WEIGHT = 0.5;
const RECENT_VIEW_DECAY = 0.03;
const RECENT_VIEW_MIN_WEIGHT = 0.15;
/** Freshness half-life in days for the recency boost. */
const FRESHNESS_DAYS = 90;

function addWeight(weights: Map<string, number>, topic: string, amount: number): void {
  if (!topic || amount <= 0) return;
  weights.set(topic, (weights.get(topic) ?? 0) + amount);
}

/**
 * Build catalog-topic weights from profile interests (mapped to Tools facet names)
 * and recent-view tags (already catalog names, decaying).
 * recentTopicLists should be ordered most recent first; one list per viewed resource.
 */
export function buildInterestWeights(
  profileTopics: string[],
  recentTopicLists: string[][]
): Map<string, number> {
  const weights = new Map<string, number>();

  const profileEnums = profileTopics.filter(isInterestTopic);
  for (const topic of catalogInterestQuery(profileEnums, []).topic) {
    addWeight(weights, topic, PROFILE_TOPIC_WEIGHT);
  }

  recentTopicLists.forEach((topics, index) => {
    const viewWeight = Math.max(RECENT_VIEW_MIN_WEIGHT, RECENT_VIEW_BASE_WEIGHT - index * RECENT_VIEW_DECAY);
    const seen = new Set<string>();
    for (const topic of topics) {
      if (!topic || seen.has(topic)) continue;
      seen.add(topic);
      addWeight(weights, topic, viewWeight);
    }
  });

  return weights;
}

export function topWeightedTopics(
  weights: Map<string, number>,
  limit = NEW_FOR_YOU_TOPIC_QUERY_LIMIT
): string[] {
  return [...weights.entries()]
    .filter(([, weight]) => weight > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([topic]) => topic);
}

export function resourceModifiedAtMs(resource: CatalogResource): number {
  return Date.parse(resource.wpModifiedAt ?? resource.wpCreatedAt ?? '') || 0;
}

/** True when the user has viewed this doc and it has not been updated since that view. */
export function isUnchangedSinceView(resource: CatalogResource, viewedAtMs: number): boolean {
  if (!Number.isFinite(viewedAtMs)) return false;
  const modified = resourceModifiedAtMs(resource);
  if (!modified) return true;
  return modified <= viewedAtMs;
}

/** Most recent viewedAt (ms) per wpId. views should be newest-first. */
export function buildLastViewedByWpId(
  views: readonly { postId: number; viewedAt: string }[]
): Map<number, number> {
  const byId = new Map<number, number>();
  for (const view of views) {
    if (byId.has(view.postId)) continue;
    const viewedAt = Date.parse(view.viewedAt);
    if (!Number.isFinite(viewedAt)) continue;
    byId.set(view.postId, viewedAt);
  }
  return byId;
}

export function scoreCatalogResource(
  resource: CatalogResource,
  weights: Map<string, number>,
  now = Date.now()
): number {
  let tagScore = 0;
  for (const topic of resource.topics ?? []) {
    tagScore += weights.get(topic) ?? 0;
  }
  if (tagScore <= 0) return 0;

  const modified = resourceModifiedAtMs(resource);
  const ageDays = modified > 0 ? Math.max(0, (now - modified) / 86_400_000) : 365;
  const freshness = Math.exp(-ageDays / FRESHNESS_DAYS);
  return tagScore * 2 + freshness;
}

export function rankNewForYou(
  candidates: CatalogResource[],
  weights: Map<string, number>,
  options?: {
    /** Drop viewed docs that have not changed since lastViewed (ms). */
    lastViewedByWpId?: ReadonlyMap<number, number>;
    limit?: number;
    now?: number;
  }
): CatalogResource[] {
  const lastViewed = options?.lastViewedByWpId;
  const limit = options?.limit ?? NEW_FOR_YOU_LIMIT;
  const now = options?.now ?? Date.now();

  return candidates
    .filter((resource) => {
      const viewedAt = lastViewed?.get(resource.wpId);
      if (viewedAt == null) return true;
      return !isUnchangedSinceView(resource, viewedAt);
    })
    .map((resource) => ({ resource, score: scoreCatalogResource(resource, weights, now) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return resourceModifiedAtMs(b.resource) - resourceModifiedAtMs(a.resource);
    })
    .slice(0, limit)
    .map((row) => row.resource);
}
